import { Clock, Mail, Phone, User, ExternalLink, Send } from "lucide-react";
import api from "../services/api";
import { useState } from "react";

interface ConsultationCardProps {
  clientName: string;
  consultationType: string;
  time: string;
  services: { name: string; category?: string; color?: string }[];
  email?: string;
  mobile?: string;
  landline?: string;
  customerId: number;
  formId: number;
  onClick?: () => void;
}

const ConsultationCard = ({
  clientName,
  consultationType,
  time,
  services,
  email,
  mobile,
  landline,
  customerId,
  formId,
  onClick,
}: ConsultationCardProps) => {
  const [resending, setResending] = useState(false);

  const handleResend = async (e: React.MouseEvent) => {
    e.stopPropagation(); // prevent opening the card

    if (resending) return;

    try {
      setResending(true);

      await api.post("/admin/consultations", {
        customer_id: customerId,
        form_id: formId,
      });

      // optional: success toast
      // toast.success("Form resent successfully");
    } catch (error) {
      console.error("Failed to resend form", error);
      // optional: toast.error("Failed to resend form");
    } finally {
      setResending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full text-left rounded-3xl bg-white p-6 transition-all hover:shadow-xl border border-gray-100 hover:border-purple-100 flex gap-6 items-start"
      aria-label={`Open consultation for ${clientName}`}
    >
      {/* Avatar */}
      <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center border border-purple-100 shrink-0 group-hover:scale-110 transition-transform">
        <User className="text-[#885ABB] w-8 h-8" />
      </div>

      <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-3 flex-1 min-w-0">
          {/* Header */}
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-xl font-black text-gray-900 truncate">
                {clientName}
              </h3>
              <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-100 shrink-0">
                <Clock size={12} className="text-gray-400" />
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">
                  {time}
                </span>
              </div>
            </div>

            <p className="text-sm font-bold text-[#885ABB] uppercase tracking-wider italic">
              {consultationType}
            </p>
          </div>

          {/* Contact Details */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-gray-500">
            {email && (
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-gray-400" />
                <span className="text-sm font-medium">{email}</span>
              </div>
            )}
            {mobile && (
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-gray-400" />
                <span className="text-sm font-medium">{mobile}</span>
              </div>
            )}
            {landline && (
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-gray-400 opacity-70" />
                <span className="text-sm font-medium">{landline}</span>
              </div>
            )}
          </div>

          {/* Services */}
          <div className="flex flex-wrap gap-2 pt-1">
            {services.map((service, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 px-3 py-1 bg-gray-50 text-gray-600 rounded-lg text-xs font-semibold border border-gray-100"
              >
                {service.color && (
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: service.color }}
                  />
                )}
                <span>{service.name}</span>
                {service.category && (
                  <span className="text-[10px] text-gray-400 border-l border-gray-200 pl-2 ml-1 uppercase">
                    {service.category}
                  </span>
                )}
              </div>
            ))}

            {services.length === 0 && (
              <span className="text-xs text-gray-400 italic">
                No services listed
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {/* Resend */}
          <div
            onClick={handleResend}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all cursor-pointer
              ${
                resending
                  ? "bg-gray-100 cursor-not-allowed"
                  : "bg-gray-50 hover:bg-[#F3EEFF]"
              }`}
            title="Resend form"
          >
            <Send
              size={18}
              className={`transition-colors ${
                resending
                  ? "text-gray-300"
                  : "text-gray-400 hover:text-[#885ABB]"
              }`}
            />
          </div>

          {/* Open */}
          <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-[#885ABB] group-hover:text-white transition-all">
            <ExternalLink
              size={18}
              className="text-gray-400 group-hover:text-white transition-colors"
            />
          </div>
        </div>
      </div>
    </button>
  );
};

export default ConsultationCard;
